import type { CSSProperties, ReactNode } from "react";

/**
 * Chip — small colored badge. Used for:
 *   • trap signatures on the reveal sheet (Design §2)
 *   • attack-lens choices in Constraint Slice
 *   • status chips in the studio (run console, quarantine)
 *
 * Variant maps to the design system's signal/trap/lens palettes
 * (CSS vars driven by data-theme).
 */
export type ChipVariant =
  | "neutral"
  | "accent"
  // Trap families (Trapline reveal sheet)
  | "trap-overclaim"
  | "trap-wrong-scope"
  | "trap-reversed-logic"
  | "trap-true-but-irrelevant"
  | "trap-tone-mismatch"
  // Attack lenses (Constraint Slice)
  | "lens-graph"
  | "lens-table"
  | "lens-substitution"
  | "lens-symmetry"
  | "lens-estimate"
  | "lens-invariant"
  // Diagnostic signals
  | "signal-cyan"
  | "signal-red"
  | "signal-green"
  | "signal-violet";

export type ChipState = "rest" | "armed" | "disabled";

export type ChipProps = {
  children: ReactNode;
  variant?: ChipVariant;
  state?: ChipState;
  dot?: boolean;
  dashed?: boolean;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

export function Chip({
  children,
  variant = "neutral",
  state = "rest",
  dot = true,
  dashed = false,
  onClick,
  className,
  style,
  ariaLabel,
}: ChipProps) {
  const interactive = !!onClick;
  const isPlainAccent = variant === "neutral" || variant === "accent";
  return (
    <span
      className={[
        "bht-chip",
        `bht-chip--${variant}`,
        `bht-chip--${state}`,
        dashed && "bht-chip--dashed",
        interactive && "bht-chip--interactive",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") onClick?.();
            }
          : undefined
      }
      aria-label={ariaLabel}
    >
      {dot && !isPlainAccent && <span className="bht-chip__dot" aria-hidden />}
      <span className="bht-chip__text">{children}</span>
    </span>
  );
}
